import _ from 'lodash'
import Backbone from 'backbone'
import reportClientId from 'scoreboard/reports/common/model/designer-client-id'
import constants from 'scoreboard/components/constants'
import ReportConstants from 'scoreboard/reports/report-constants'
const { DATASET_REPORT_TYPE } = ReportConstants
import * as AggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
const { listUniqueValues, useEquation } = AggregationTypeOptions
import ThresholdColorModel from 'scoreboard/components/threshold-colors/threshold-colors-model'
import moveDecimal from '../../../components/move-decimal'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import {isColumnScored} from '../../scorecard-report/scorecard-report-columns'

const formattingProperties = ['bold', 'italic', 'underlined', 'justified', 'precision', 'colorType', 'colorStyle', 'thresholdColoring', 'fontSize']

/**
 * Returns the actual property used to store the reference to the underlying field
 * or column type depending on the type of report
 */
function fieldIdOrColumnTypeProperty(domainType) {
	return domainType == DATASET_REPORT_TYPE ? 'datasetFieldId' : 'columnType'
}

/**
 * Client model of the ReportColumn in the report
 */
class ReportColumn extends Backbone.Model {

	initialize(attrs = {}, options = {}) {
		this.options = options
		//ensure the unique id for this column is always present in the attributes
		//this is so backbone can match the columns do when merging the response
		//into the existing column model collection, as well as lining up data
		//for a given column
		this.attributes.cid = attrs.cid || this.cid

		this.on('change:columnType', (model, value, opts) => {
			if (this.has('sortType') && !this.canSortBy()) {
				//if we're changing to a column that can't be sorted and sorting
				//is enabled, automatically remove the sort
				this.unset('sortType', {silent: true})
			}

			this._validateColorType()
		})

		this.on('change:datasetFieldId', (model,value, opts) => {
			this._validateColorType()
		})

		this.on('change:label', (model, value, opts) => {
			const hasEquation = !_.isNil(this.get('equation'))
			const useDefaultLabel = !this.get('customLabel')
			if (hasEquation && useDefaultLabel && value.length > 2000) {
				//#44767 if equation is longer than max 2000 char, auto truncate the label
				this.attributes.label = value.substr(0,1997) + '...'
			}
		})
	}

	fieldIdOrColumnType(value, opts = {}) {
		if (!_.isNil(value)) {
			this.set(fieldIdOrColumnTypeProperty(this.options.reportDomainType), value, opts)
			return
		}
		return this.get(fieldIdOrColumnTypeProperty(this.options.reportDomainType))
	}

	defaultColumnData(reportModel) {
		return reportModel.columnDataByFieldIdOrColumnType(this.fieldIdOrColumnType())
	}

	columnClientId() {
		return reportClientId(this)
	}

	allowedOutsideOfRepeatBy() {
		//do not allow scored scorecard fields to be moved outside of repeat bys
		return this.isScored() ? false : true
	}

	isScored() {
		let { reportDomainType } = this.options
		return reportDomainType == 'scorecard-item'
			&& isColumnScored(this.get('columnType'))
	}

	isGrouping() {
		return !_.isNil(this.get('groupingId'))
	}

	isSorting(reportModel) {
		return !_.isNil(this.columnSortType(reportModel))
	}

	/**
	 * Returns the sortType if this column is sorted
	 */
	columnSortType(reportModel) {
		if (this.isGrouping() && !this.isAggregatedGroupedColumn(reportModel)) {
			return this.get('groupingSortType')
		}
		return this.get('sortType')
	}

	isAggregatedGroupedColumn(reportModel) {
		//#44318 when aggregating and grouping, the last grouped column shows the sort
		//options as if it were ungrouped since it's no longer in a grouping header in this
		//situation, we basically treat this grouped column from a UI perspective as an ungrouped
		//column for the purposes of sorting
		return this.isGrouping() && !reportModel.isShowingIndividualRecords()
			&& reportModel.groupByPosition(this) == reportModel.get('groupBy').length - 1
	}

	_nonDatasetColumnTypeData() {
		let { reportDomainType } = this.options
		if (reportDomainType == 'scorecard-item') {
			return constants.scorecardReportColumnTypes
		} else {
			return constants.initiativeReportColumnTypes
		}
	}

	canGroupBy() {
		let { reportDomainType } = this.options
		if (reportDomainType == DATASET_REPORT_TYPE) {
			return true		//dataset report columns can always group
		}
		let columnData = this._nonDatasetColumnTypeData()
		return columnData[this.fieldIdOrColumnType()].canGroupBy
	}

	canSortBy() {
		let { reportDomainType } = this.options
		if (reportDomainType == DATASET_REPORT_TYPE) {
			return true		//dataset report columns can always sort
		}
		let columnData = this._nonDatasetColumnTypeData()
		return !columnData[this.fieldIdOrColumnType()].unsortable || false
	}

	//no-op because we're using this solely as a nested model
	sync() {}

	/**
	 * Returns the data type taking into account aggregation if the model is
	 * aggregation
	 */
	resolveDataType(reportModel) {
		const columnDataType = this.get('coercedDataType') || this.get('dataType')
		if (reportModel.isAggregating()) {
			switch (this.get('aggregationType')) {
				case listUniqueValues.id:
				case useEquation.id: return 'text'
				default: return columnDataType
			}
		}
		return columnDataType
	}

	showHierarchyIndentOption(reportModel) {
		const defaultColumnData = this.defaultColumnData(reportModel)
		const supportsIndentHierarchy = _.get(defaultColumnData, 'supportsIndentHierarchy', false)
		const supportsHierarchyOrdering = _.get(defaultColumnData, 'supportsHierarchyOrdering', false)
		return supportsIndentHierarchy && supportsHierarchyOrdering
			&& !this.isGrouping()
	}

	validateHierarchyIndent(reportModel, stack) {
		const isNotShowingIndividualRecords = this.isGrouping() &&
			!reportModel.isShowingIndividualRecords()
		if (isNotShowingIndividualRecords) {
			return 'report.designer.indentRequiresShowIndividualRecords'
		}
		const isRepeatByStack = stack.isRepeatByStack()
		if (isRepeatByStack) {
			return 'report.designer.indentRequiresBasicStack'
		}
		return null
	}

	requiresAggregationType(reportModel) {
		let fieldIdOrColumnType = this.get(fieldIdOrColumnTypeProperty(reportModel.reportDomainType))
		let aggregatedColumn = reportModel.isAggregating() &&
			!reportModel.isGroupingByFieldIdOrColumnType(fieldIdOrColumnType)

		let columnData = reportModel._defaultColumnData(fieldIdOrColumnType)
		let defaultAggregationType = _.get(columnData, 'defaultAggregationType', null)
		let foreignFieldOnManySide = reportModel.reportDomainType == DATASET_REPORT_TYPE &&
			_.get(columnData, 'foreignFieldOnManySide', false)

		//#40816 show aggregation type when grouping/hiding individual records and
		//the target column isn't the one being grouped and agg type
		//also needed for datasets if it's a foriegn field on the many side where
		//the aggregation type is not none
		return aggregatedColumn || (foreignFieldOnManySide && defaultAggregationType != 'none')
	}

	colorModel(dataType) {
		if (this.get('colorType') == 'conditional') {
			let {min, max} = this.attributes
			let thresholdColoring = this.get('thresholdColoring')
			if (dataType == 'percent') {
				min =  moveDecimal(min, 2)
				max =  moveDecimal(max, 2)
				// avoid side effects
				thresholdColoring = _.clone(thresholdColoring)
				_.extend(thresholdColoring, { min, max }) // #60093
			}
			return new ThresholdColorModel(_.extend({min, max}, thresholdColoring))
		} else {
			return null
		}
	}

	_validateColorType() {
		let prevValue = this.previous('colorType')
		//only keep scoring/conditionals if the new column type supports it
		if (prevValue == 'scoring' && !this.isScored()) {
			this.unset('colorType', {silent: true})
		}
		if (prevValue == 'conditional' && !isNumericDataType(this.get('dataType'))) {
			this.unset('colorType', {silent: true})
		}
	}

}

_.extend(ReportColumn, {
	fieldIdOrColumnTypeProperty
})
export default ReportColumn
export { fieldIdOrColumnTypeProperty }
ReportColumn.__filename__ = 'reports/common/model/designer-column.js'
